/**
 * ProductTestPage - Test page for ProductDescriptionSection and TasksTable
 *
 * Development-only route for testing product page components during migration.
 * Features:
 * - AutoLogin wrapper for automatic authentication
 * - Hardcoded project ID for consistent testing
 * - Full-screen layout for immersive testing
 * - Production guard (redirect to / in production)
 * - Real ProductDescriptionSection component from src/modules/product
 *
 * URL: /product-test
 *
 * Sprint: sprint-6 (013-product-page-typescript)
 * Requirements: FR-000
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AutoLogin } from './AutoLogin';
import { ProductDescriptionSection } from '@modules/product/components/ProductDescriptionSection';
import { NotificationProvider } from '@modules/product/contexts/NotificationContext';
import { ProjectService } from '@modules/firebase-core/services/ProjectService';
import type { Project, Task } from '@segmently/schema-models/entities';
import { useAuth } from '../../contexts/AuthContext';

// Test constants - hardcoded for consistent testing
const TEST_PROJECT_ID = 'JfQI6dIKYOTUcg1RiCE0';

/**
 * Inner component that uses auth context after AutoLogin
 */
const ProductTestPageInner: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // State for project data
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ProjectService instance
  const projectService = useMemo(() => new ProjectService(), []);

  // Fetch project data
  const fetchProjectData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Get project data
      const projectData = await projectService.get(TEST_PROJECT_ID);
      setProject(projectData);

      // Get tasks from project's tasks subcollection
      const tasksService = projectService.getTasksService(TEST_PROJECT_ID);
      const tasksData = await tasksService.list();
      setTasks(tasksData);

    } catch (err) {
      console.error('Error fetching project data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project data');
    } finally {
      setLoading(false);
    }
  }, [currentUser, projectService]);

  // Fetch project data on mount
  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Production guard - redirect to home in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      navigate('/');
    }
  }, [navigate]);

  // Callbacks for ProductDescriptionSection
  const handleDescriptionSaved = useCallback(() => {
    console.log('[ProductTestPage] Description saved');
    fetchProjectData();
  }, [fetchProjectData]);

  const handleAudiencesGenerated = useCallback(() => {
    console.log('[ProductTestPage] Audiences generated');
    fetchProjectData();
  }, [fetchProjectData]);

  const handleCreateAudiences = useCallback(async (audienceCount: number, selectedTaskIds: string[]) => {
    console.log('[ProductTestPage] Create audiences:', { audienceCount, selectedTaskIds });
    // TODO: Implement actual audience creation
  }, []);

  const handleUpdateInterviewQuestions = useCallback(async () => {
    console.log('[ProductTestPage] Update interview questions');
    // TODO: Implement actual interview questions update
  }, []);

  const handleDeleteTasks = useCallback(async (selectedTaskIds: string[]) => {
    console.log('[ProductTestPage] Delete tasks:', { selectedTaskIds });
    const tasksService = projectService.getTasksService(TEST_PROJECT_ID);

    // Delete all selected tasks in parallel
    await Promise.all(selectedTaskIds.map(taskId => tasksService.delete(taskId)));

    await fetchProjectData();
  }, [projectService, fetchProjectData]);

  // ============================================================================
  // TasksTable handlers (Sprint 6)
  // ============================================================================

  const handleCreateTasksFromDescription = useCallback(async () => {
    console.log('[ProductTestPage] Create tasks from description');
    // TODO: Implement via generateTasksFromDescription API
  }, []);

  const handleCreateTask = useCallback(async (taskData: Partial<Task>) => {
    console.log('[ProductTestPage] Create task:', taskData);
    const tasksService = projectService.getTasksService(TEST_PROJECT_ID);
    await tasksService.create(taskData as Task);
    await fetchProjectData();
  }, [projectService, fetchProjectData]);

  const handleUpdateTask = useCallback(async (taskId: string, taskData: Partial<Task>) => {
    console.log('[ProductTestPage] Update task:', { taskId, taskData });
    const tasksService = projectService.getTasksService(TEST_PROJECT_ID);
    await tasksService.update(taskId, taskData);
    await fetchProjectData();
  }, [projectService, fetchProjectData]);

  const handleDeleteTask = useCallback(async (taskId: string) => {
    console.log('[ProductTestPage] Delete task:', taskId);
    const tasksService = projectService.getTasksService(TEST_PROJECT_ID);
    await tasksService.delete(taskId);
    await fetchProjectData();
  }, [projectService, fetchProjectData]);

  const handleDuplicateTask = useCallback(async (task: Task) => {
    console.log('[ProductTestPage] Duplicate task:', task);
    const tasksService = projectService.getTasksService(TEST_PROJECT_ID);
    const { id, createdAt, updatedAt, ...taskDataToClone } = task;
    await tasksService.create({
      ...taskDataToClone,
      name: `${task.name} (Copy)`,
    } as Task);
    await fetchProjectData();
  }, [projectService, fetchProjectData]);

  const handleVariablesChange = useCallback(async (variables: Record<string, any>, reviewed: Record<string, any>) => {
    const { VariableEditingService } = await import('@modules/firebase-core/services/VariableEditingService');
    await new VariableEditingService().saveProject(TEST_PROJECT_ID, variables, reviewed);
    await fetchProjectData();
  }, [fetchProjectData]);

  const handleGenerateAudience = useCallback(async (task: Task) => {
    console.log('[ProductTestPage] Generate audience for task:', task);
    // TODO: Implement via fetchAudiencesFromDescriptionWithProxy API
  }, []);

  // Early return if production - don't render any content
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <Box
        data-testid="product-test-root"
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        data-testid="product-test-root"
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="caption" color="text.secondary">
          Project ID: {TEST_PROJECT_ID}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="product-test-root"
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'auto',
        p: 2,
      }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        Product Test Page
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
        Development-only test page for product components. Project ID: {TEST_PROJECT_ID}
      </Typography>

      {/* Real ProductDescriptionSection component */}
      <Box data-testid="product-description-section" sx={{ flex: 1 }}>
        <ProductDescriptionSection
          project={project}
          projectId={TEST_PROJECT_ID}
          currentUser={currentUser ? { uid: currentUser.uid, email: currentUser.email || undefined } : undefined}
          onDescriptionAndSettingsSaved={handleDescriptionSaved}
          onAudiencesGenerated={handleAudiencesGenerated}
          fetchProjectData={fetchProjectData}
          tasks={tasks}
          onCreateAudiences={handleCreateAudiences}
          onUpdateInterviewQuestions={handleUpdateInterviewQuestions}
          onDeleteTasks={handleDeleteTasks}
          // TasksTable handlers
          onCreateTasksFromDescription={handleCreateTasksFromDescription}
          onCreateTask={handleCreateTask}
          onUpdateTask={handleUpdateTask}
          onDeleteTask={handleDeleteTask}
          onDuplicateTask={handleDuplicateTask}
          onGenerateAudience={handleGenerateAudience}
          variables={(project as any)?.variableSet?.variables ?? {}}
          onVariablesChange={handleVariablesChange}
        />
      </Box>
    </Box>
  );
};

const ProductTestPage: React.FC = () => {
  return (
    <AutoLogin>
      <NotificationProvider>
        <ProductTestPageInner />
      </NotificationProvider>
    </AutoLogin>
  );
};

export default ProductTestPage;
